import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

const PALETTE = [
    '#ABBB7D', '#5C6BC0', '#EF6C00', '#26A69A', '#AB47BC',
    '#EC407A', '#42A5F5', '#D4AC0D', '#7E57C2', '#66BB6A',
    '#FF7043', '#29B6F6', '#8D6E63', '#C0CA33', '#78909C',
    '#F06292', '#9CCC65', '#FFA726', '#5E35B1', '#00ACC1'
];

const tenantColor = (index) => PALETTE[index % PALETTE.length];

function formatMonthLabel(key){
    const [year, month] = key.split('-');
    const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
    return date.toLocaleString('default', { month: 'short', year: '2-digit' });
}

function ActivityTooltip({ active, payload, label, tenantsById }){
    if(!active || !payload || payload.length === 0){
        return null;
    }
    const rows = payload
        .filter(p => p.value != null && p.value > 0)
        .sort((a, b) => b.value - a.value);
    return (
        <Paper
            elevation={4}
            sx={{
                p: 1.25,
                fontSize: '0.85em',
                maxHeight: 320,
                overflowY: 'auto',
                color: 'text.primary',
                backgroundColor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                minWidth: 220
            }}
        >
            <Box sx={{ fontWeight: 600, mb: 0.5 }}>{formatMonthLabel(label)}</Box>
            {rows.map(r => {
                const tenant = tenantsById[r.dataKey];
                return (
                    <Box key={r.dataKey} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: '2px' }}>
                        <Box component="span" sx={{
                            display: 'inline-block',
                            width: 10,
                            height: 10,
                            backgroundColor: r.color,
                            borderRadius: '2px',
                            flexShrink: 0
                        }} />
                        <Box component="span" sx={{ flex: 1 }}>{tenant ? tenant.name : r.dataKey}</Box>
                        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {r.value.toLocaleString()} logs
                        </Box>
                        {tenant != null && (
                            <Box component="span" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                                · {tenant.licenseCount} lic
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Paper>
    );
}

export default function TenantActivityChart(){
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get('/int/tenantActivityReport');
                if(!cancelled) setData(res.data);
            } catch (err) {
                if(!cancelled) setError(err);
            } finally {
                if(!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const { chartData, tenants, tenantsById } = useMemo(() => {
        if(!data) return { chartData: [], tenants: [], tenantsById: {} };
        const byId = {};
        for(const t of data.tenants){
            byId[t.id] = t;
        }
        const rows = data.months.map(m => {
            const row = { month: m.key };
            for(const t of data.tenants){
                row[t.id] = t.monthly[m.key] || 0;
            }
            return row;
        });
        return { chartData: rows, tenants: data.tenants, tenantsById: byId };
    }, [data]);

    if(loading){
        return (
            <div style={{ padding: 25, minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Loading tenant activity…
            </div>
        );
    }
    if(error){
        return (
            <div style={{ padding: 25, color: 'red' }}>
                Error loading tenant activity: {error.message}
            </div>
        );
    }
    if(!data || tenants.length === 0){
        return (
            <div style={{ padding: 25, color: '#666' }}>
                No tenant activity data yet. The report runs monthly — trigger it via the job queue to populate.
            </div>
        );
    }

    return (
        <div style={{ width: '100%', padding: '25px' }}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Tenant activity — last {data.months.length} months
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Stacked log volume per tenant per month. Tooltip shows current license count.
                </Typography>
            </Box>
            <ResponsiveContainer width="100%" height={380}>
                <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                        dataKey="month"
                        tick={{ fontSize: 12 }}
                        tickFormatter={formatMonthLabel}
                        interval={0}
                        angle={-35}
                        textAnchor="end"
                        height={60}
                    />
                    <YAxis
                        tick={{ fontSize: 12 }}
                        allowDecimals={false}
                        width={60}
                        tickFormatter={(v) => v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}
                    />
                    <Tooltip content={<ActivityTooltip tenantsById={tenantsById} />} />
                    <Legend
                        wrapperStyle={{ fontSize: '0.75em', paddingTop: 8 }}
                        iconSize={10}
                        formatter={(value) => {
                            const t = tenantsById[value];
                            return t ? `${t.name} (${t.licenseCount})` : value;
                        }}
                    />
                    {tenants.map((t, idx) => (
                        <Bar
                            key={t.id}
                            dataKey={t.id}
                            name={t.id}
                            stackId="tenants"
                            fill={tenantColor(idx)}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}
